import { Component, type ErrorInfo, type ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

/**
 * Class-component error boundary that catches render-time throws from any
 * descendant. Shows a friendly fallback with a Reload button and a collapsible
 * detail block instead of blanking the whole app.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught error:', error, info)
  }

  override render() {
    if (this.state.hasError) {
      const { error } = this.state
      return (
        <div
          data-testid="error-boundary-fallback"
          className="flex h-full min-h-[200px] flex-col items-center justify-center gap-4 p-8 text-center"
        >
          <h2 className="text-lg font-semibold text-stone-800 dark:text-stone-200">
            Something went wrong
          </h2>
          <p className="text-sm text-stone-600 dark:text-stone-400">
            An unexpected error occurred. You can try reloading the page.
          </p>
          <button
            data-testid="error-boundary-reload"
            type="button"
            onClick={() => location.reload()}
            className="rounded-md bg-stone-800 px-4 py-2 text-sm font-medium text-white hover:bg-stone-700 dark:bg-stone-200 dark:text-stone-900 dark:hover:bg-stone-300"
          >
            Reload
          </button>
          {error && (
            <details className="mt-2 max-w-lg text-left text-xs text-stone-500 dark:text-stone-400">
              <summary className="cursor-pointer select-none">Error detail</summary>
              <pre className="mt-2 whitespace-pre-wrap break-all rounded bg-stone-100 p-2 dark:bg-stone-800">
                {error.message}
                {error.stack ? `\n\n${error.stack}` : ''}
              </pre>
            </details>
          )}
        </div>
      )
    }

    return this.props.children
  }
}
